import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { logo } from "../../assets";
import {
  activate_admin_account_api,
  verify_activation_token_api,
} from "../../DAL/Auth/AdminActivation";
import backgroundImage from "../../assets/images/image.png";

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    color: "#1a1a1a",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: "#1a1a1a" },
  "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

export default function AdminActivation() {
  const { enqueueSnackbar } = useSnackbar();
  const { setUserInfo } = useContentSetting();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [adminInfo, setAdminInfo] = useState({
    email: "",
    firstName: "",
    lastName: "",
    schoolName: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const getActivationData = () => {
    const urlToken = searchParams.get("token");
    const urlSchoolId = searchParams.get("school_id");
    if (urlToken && urlSchoolId) {
      return { token: urlToken, schoolId: urlSchoolId };
    }
    const stored = sessionStorage.getItem("pending_activation");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  };

  const activationData = getActivationData();
  const activationToken = activationData?.token;
  const flightSchoolId = activationData?.schoolId;

  useEffect(() => {
    validateActivationToken();
    return () => {
      sessionStorage.removeItem("pending_activation");
    };
  }, []);

  const validateActivationToken = async () => {
    if (!activationToken || !flightSchoolId) {
      setIsValidToken(false);
      setIsLoading(false);
      enqueueSnackbar("Invalid activation link", { variant: "error" });
      return;
    }

    try {
      const result = await verify_activation_token_api(activationToken, flightSchoolId);
      
      if (result.valid && result.admin && result.school) {
        setAdminInfo({
          email: result.admin.email,
          firstName: result.admin.firstName,
          lastName: result.admin.lastName,
          schoolName: result.school.name,
        });
        setIsValidToken(true);
      } else {
        setIsValidToken(false);
        enqueueSnackbar("Invalid or expired activation link", {
          variant: "error",
        });
      }
    } catch (error) {
      setIsValidToken(false);
      setIsLoading(false);
      enqueueSnackbar(
        error.message || "Invalid or expired activation link",
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.password.trim()) {
      enqueueSnackbar("Password is required", { variant: "error" });
      return;
    }

    if (formData.password.length < 8) {
      enqueueSnackbar("Password must be at least 8 characters long", {
        variant: "error",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      enqueueSnackbar("Passwords do not match", { variant: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      const data = {
        token: activationToken,
        school_id: flightSchoolId,
        password: formData.password,
      };

      const result = await activate_admin_account_api(data);

      if (result.success && result.token && result.admin) {
        enqueueSnackbar("Account activated successfully", {
          variant: "success",
        });
        // Store auth token and admin info
        localStorage.setItem("token", result.token);
        localStorage.setItem("user_data", JSON.stringify(result.admin));
        setUserInfo(result.admin);

        // Redirect to school portal after 1 second
        setTimeout(() => {
          navigate("/school/dashboard");
        }, 1000);
      } else {
        enqueueSnackbar(
          result.message || "Failed to activate account",
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Activation error:", error);
      enqueueSnackbar(
        error.message || "An error occurred during activation",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentDate = new Date();
  const currentHour = currentDate.getHours();
  const showBackgroundImage = currentHour < 12;

  if (isLoading) {
    return (
      <div
        className="login-card"
        style={{
          backgroundImage: showBackgroundImage ? `url(${backgroundImage})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="theme-card p-4">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress color="primary" />
          </div>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div
        className="login-card"
        style={{
          backgroundImage: showBackgroundImage ? `url(${backgroundImage})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="theme-card p-4" style={{ maxWidth: "400px" }}>
          <div className="login-logo light-border-bottom p-3">
            <img src={logo} alt="" className="app-logo" />
          </div>
          <div className="login-form p-3">
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Invalid or expired activation link. Please contact your administrator
                for a new activation link.
              </Typography>
            </Alert>
          </div>
          <div className="developed-by">
            <p>XAir Operations Interface</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="login-card"
      style={{
        backgroundImage: showBackgroundImage ? `url(${backgroundImage})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div className="theme-card p-4" style={{ maxWidth: "450px", width: "100%" }}>
        <div className="login-logo light-border-bottom p-3">
          <img src={logo} alt="" className="app-logo" />
        </div>
        <div className="login-form">
          <p className="mt-3">Activate Your Admin Account</p>
          {isValidToken && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: "rgba(192,30,46,0.07)", borderRadius: "9px", border: "1px solid rgba(192,30,46,0.18)" }}>
              <Typography variant="body2" sx={{ color: "#1a1a1a", mb: 1, fontFamily: "'Rubik', sans-serif" }}>
                <strong>School:</strong> {adminInfo.schoolName}
              </Typography>
              <Typography variant="body2" sx={{ color: "#1a1a1a", mb: 1, fontFamily: "'Rubik', sans-serif" }}>
                <strong>Name:</strong> {adminInfo.firstName} {adminInfo.lastName}
              </Typography>
              <Typography variant="body2" sx={{ color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>
                <strong>Email:</strong> {adminInfo.email}
              </Typography>
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{ color: "#6b6b6b", mb: 3, fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}
          >
            Set your password to activate your account and get started with
            managing your flight school.
          </Typography>

          <form onSubmit={handleSubmit}>
            <div className="row input-form">
              <div className="col-12">
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  id="password"
                  label="Password"
                  variant="outlined"
                  name="password"
                  value={formData.password}
                  onChange={handleChange("password")}
                  sx={inputStyles}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          className="visibility-password"
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#aaa", display: "block", mt: 1, fontFamily: "'Rubik', sans-serif" }}
                >
                  Must be at least 8 characters long
                </Typography>
              </div>

              <div className="col-12 mt-4">
                <TextField
                  fullWidth
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm-password"
                  label="Confirm Password"
                  variant="outlined"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  sx={inputStyles}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowConfirmPassword}
                          className="visibility-password"
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>

            <div className="add--button login-button-box text-end mt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  opacity: isSubmitting ? 0.6 : 1,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : "Activate Account"}
              </button>
            </div>
          </form>

          <Typography
            variant="caption"
            sx={{ color: "#aaa", display: "block", textAlign: "center", mt: 3, fontFamily: "'Rubik', sans-serif" }}
          >
            After activation, you'll be able to log in with your email and password.
          </Typography>
        </div>
        <div className="developed-by">
          <p>XAir Operations Interface</p>
        </div>
      </div>
    </div>
  );
}
